import * as React from 'react';
import { Component, Fragment, ReactNode } from 'react';
import { action, observable, computed } from 'mobx';
import { observer, inject } from 'mobx-react';
import styled, { withTheme } from 'styled-components';
import { propStyles } from '@kurtosys/ksys-portal-components/build/utils/propStyles';
import DownloadControls from './DownloadControls';
import DocumentMenuTitleControls from './DocumentMenuTitleControls';
import DocumentMenuList from './DocumentMenuList';
import { DataStore } from '../../stores';
import { createPortal } from 'react-dom';
import Translate from '../Translate';
import { DisplayOption } from '../../types';
import InlineDisclaimers from '../InlineDisclaimers';

const DropDownWrapper = styled<any, any>('div')`
    ${propStyles};
    position: relative;
`;

DropDownWrapper.defaultProps = {
    display: 'inline-flex',
};

interface DocumentMenuTheme {
    DropDownWrapper?: any;
    DocumentMenuList?: any;
    DocumentMenuTitleControls?: any;
    DownloadControls?: any;
}

interface DocumentMenuProps {
    theme: any;
    dataStore: DataStore;
    documents: Array<any>;
    documentGroupLabel?: string;
    showSingleDocument?: boolean;
    showSingleDocumentLabel?: boolean;
    showSingleDocumentGroupLabel?: boolean;
    showMenuTitle?: boolean;
    showCount?: boolean;
    showCheckbox?: boolean;
    documentMenuTheme: DocumentMenuTheme;
    displayOption: DisplayOption;
    metricContext?: string;
    entityWrapper?: any;
    selected?(clientCode: string): boolean;
    onChange?(download: any): any;
}

@inject('dataStore')
@observer
class DocumentMenu extends Component<DocumentMenuProps> {
    @observable open = false;

    @observable target;
    @observable content;

    @observable top: string = '100%';
    @observable right: string = '0';

    constructor(props: DocumentMenuProps) {
        super(props);
        this.handleOnChange = this.handleOnChange.bind(this);
    }

    @computed
    get theme() {
        const { theme } = this.props;
        const componentTheme: DocumentMenuTheme = {};
        return { ...theme, ...componentTheme };
    }

    @action
    refTarget = ref => {
        this.target = ref;
    };

    @action
    refContent = ref => {
        this.content = ref;
    };

    @action
    toggleDropdown = () => {
        const { dataStore } = this.props;
        this.open = !this.open;
        // calculate context menu position
        if (this.open && dataStore.isMobile) {
            const parentRect = document
                .getElementById('fundcenter-context-menu')
                .getBoundingClientRect();
            const elemRect = this.target.getBoundingClientRect();
            const top = elemRect.top + elemRect.height - parentRect.top;
            // See: https://stackoverflow.com/questions/1248081/get-the-browser-viewport-dimensions-with-javascript#answer-8876069
            const right =
                Math.max(
                    document.documentElement.clientWidth,
                    window.innerWidth || 0
                ) - elemRect.right;
            this.top = `${top}px`;
            this.right = `${right}px`;
        }
    };

    @action
    handleOnChange(document) {
        const { onChange } = this.props;
        if (onChange) {
            onChange(document);
        }
    }

    @computed
    get accessibilityContextForIcon(): string | undefined {
        const { documents } = this.props;
        let docTitle;
        const document = documents && documents.find(doc => doc && doc.title);
        if (document) {
            docTitle = `for ${document.title}`;
        }
        return docTitle;
    }

    @computed
    get displayOption(): DisplayOption {
        const { displayOption = 'dropdown' } = this.props;
        return displayOption;
    }

    @computed
    get showSingleDocument(): boolean {
        const { documents = [], showSingleDocument = false } = this.props;
        return showSingleDocument && documents.length === 1;
    }

    /**
     * Used to identify document type for disclaimers
     *
     * @readonly
     */
    @computed
    get documentType(): string {
        const { documents, dataStore } = this.props;
        const { documentGroupMeta } = dataStore.config;
        const documentTypeProperty = documentGroupMeta || 'document_type';
        return (
            documents.length > 0 &&
            documents[0].meta &&
            documents[0].meta[documentTypeProperty] &&
            documents[0].meta[documentTypeProperty].value
        );
    }

    @computed
    get documentGroups(): ReactNode {
        const {
            documents = [],
            children,
            showCount = false,
            documentGroupLabel,
            entityWrapper,
            theme,
            dataStore,
        } = this.props;

        let conditionalDisclaimers;
        if (dataStore && dataStore.disclaimerStore) {
            conditionalDisclaimers = dataStore.disclaimerStore.getConditionalDisclaimersForDocument(
                this.documentType
            );
        }

        const dropDownGroup = (
            <Fragment>
                <DocumentMenuTitleControls
                    documents={documents}
                    onClick={this.toggleDropdown}
                    showCount={showCount}
                    refTarget={this.refTarget}
                    documentGroupLabel={documentGroupLabel}
                    open={this.open}
                    theme={theme}
                >
                    {children}
                </DocumentMenuTitleControls>
                {this.dropDownMenu}
            </Fragment>
        );
        // Wrap drop down component for popup styling
        const component =
            this.displayOption === 'dropdown' ? (
                <DropDownWrapper>{dropDownGroup}</DropDownWrapper>
            ) : (
                dropDownGroup
            );

        return (
            <Fragment>
                {documentGroupLabel && (
                    <InlineDisclaimers
                        entityWrapper={entityWrapper}
                        conditionalDisclaimers={conditionalDisclaimers}
                    >
                        <Translate>{documentGroupLabel}</Translate>
                    </InlineDisclaimers>
                )}
                {component}
            </Fragment>
        );
    }

    @computed
    get dropDownMenu(): ReactNode {
        const { dataStore } = this.props;
        // Use portal to pull context menu out of swipe tabs in mobile which was causing it to get cut off
        let dropDownMenu;
        if (dataStore.isMobile && this.displayOption === 'dropdown') {
            const node =
                document && document.getElementById('fundcenter-context-menu');
            dropDownMenu = createPortal(this.documentMenuList, node);
        } else {
            dropDownMenu = this.documentMenuList;
        }
        return dropDownMenu;
    }

    @computed
    get documentMenuList(): ReactNode {
        const {
            documents = [],
            showCheckbox = false,
            onChange,
            selected,
            theme,
            metricContext,
            entityWrapper,
        } = this.props;

        return (
            <DocumentMenuList
                documents={documents}
                open={this.open}
                showLabel={true}
                showCheckbox={showCheckbox}
                onChange={onChange}
                toggleDropdown={this.toggleDropdown}
                selected={selected}
                top={this.top}
                right={this.right}
                refContent={this.refContent}
                displayOption={this.displayOption}
                theme={theme}
                metricContext={`documentMenu-${metricContext}`}
                entityWrapper={entityWrapper}
                documentType={this.documentType}
            />
        );
    }

    @computed
    get document() {
        const {
            documents = [],
            dataStore,
            selected,
            theme,
            showSingleDocumentLabel,
            showSingleDocumentGroupLabel,
            showCheckbox = false,
            documentGroupLabel,
            metricContext,
            entityWrapper,
        } = this.props;
        if (!dataStore) {
            return null;
        }
        const {
            config,
            getDocumentDisplayName,
            downloadDocuments,
            getDocumentLink,
            previewDocuments,
            isMobile,
            getFileName,
        } = dataStore;
        const { documentPreviewEnabled, documentPreviewIcon } = config;
        const document = documents[0];
        return (
            <DownloadControls
                label={getDocumentDisplayName(document)}
                groupLabel={documentGroupLabel}
                showLabel={showSingleDocumentLabel}
                showGroupLabel={showSingleDocumentGroupLabel}
                showPreview={documentPreviewEnabled}
                selected={selected && selected(document.clientCode)}
                onChange={() => this.handleOnChange(document)}
                onClick={() => downloadDocuments([document])}
                href={getDocumentLink([document])}
                onPreviewClick={e => {
                    e.stopPropagation();
                    previewDocuments([document]);
                }}
                showCount={false}
                theme={{
                    DownloadButton: {
                        flex: '0 0 auto',
                    },
                    ...theme,
                }}
                isMobile={isMobile}
                fileName={getFileName([document])}
                previewButtonFileName={documentPreviewIcon}
                showCheckbox={showCheckbox}
                metricContext={`documentMenu-${metricContext}`}
                entityWrapper={entityWrapper}
                documentType={this.documentType}
                document={document}
            />
        );
    }

    render() {
        return this.showSingleDocument ? this.document : this.documentGroups;
    }
}

export default withTheme(DocumentMenu) as any;



// WEBPACK FOOTER //
// ./src/Components/Document/DocumentMenu.tsx